import React, { useCallback, useState } from 'react';
import { styled } from '@this/constants/themes';
import _ from 'lodash';

import A from '@this/shared/atoms/a';
import type { FilterableSelectorOption } from '@this/shared/filterable_selector/filterable_selector';

import type ReserveInfo from '@this/domain/reserve_info2';
import OrderItemMapping from '@this/domain/order_item_mapping2';
import InputCustomerInformationFilterableSelector from '../input_customer_information_filterable_selector/input_customer_information_filterable_selector';

type Props = {
  reserveInfo: ReserveInfo;
  workflowFormWidth: string;
};

const DETAIL_FORM_WIDTH = '240px';

const ExpensesAccountTypeSection: React.FC<Props> = ({ reserveInfo, workflowFormWidth }) => {
  const [showDetail, setShowDetail] = useState(false);

  const selectedOption = (): FilterableSelectorOption | null => {
    const mapping = reserveInfo.orderItemMappingsExpensesPart.find(
      m => m.travelerId === -1 && m.orderItemType === 'all' && m.orderItemIndex === -1
    );
    if (mapping?.selectedOptionLabel) {
      return {
        label: mapping.selectedOptionLabel,
        value: '',
        matcher: ''
      };
    }
    return null;
  };

  const onSelectDetail = useCallback(
    (changingMapping: OrderItemMapping, option: FilterableSelectorOption | null) => {
      const values = option?.value.split('-');
      if (values && values?.length === 2 && values[0] === 'expenses') {
        changingMapping.expensesAccountTypeId = Number(values[1]);
        changingMapping.selectedOptionLabel = option?.label || '';
      } else {
        changingMapping.expensesAccountTypeId = undefined;
        changingMapping.selectedOptionLabel = '';
      }
    },
    []
  );

  const onSelect = useCallback(
    (option: FilterableSelectorOption | null) => {
      const values = option?.value.split('-');
      if (values && values?.length === 2) {
        reserveInfo.expensesAccountTypeId = Number(values[1]);
        reserveInfo.orderItemMappingsExpensesPart = [
          new OrderItemMapping({
            travelerId: -1,
            orderItemType: 'all',
            orderItemIndex: -1,
            expensesAccountTypeId: Number(values[1]),
            selectedOptionLabel: option?.label
          })
        ];
      } else {
        reserveInfo.expensesAccountTypeId = undefined;
        reserveInfo.orderItemMappingsExpensesPart = [];
      }
    },
    [reserveInfo]
  );

  return (
    <>
      <Label>
        勘定科目
        {reserveInfo.expensesAccountTypeAvailable && reserveInfo.isExpensesAccountTypeRequired && <Red>*</Red>}
      </Label>
      {showDetail ? (
        <ShareDetailArea workflowFormWidth={workflowFormWidth}>
          {reserveInfo.expensesAccountTypeDetailMode === 'item' ? (
            <>
              <ShareDetailFlexArea>
                <ShareDetailModeSelected>商品ごと</ShareDetailModeSelected>
                <ShareDetailModeLinkArea>
                  <A
                    onClick={() => {
                      reserveInfo.expensesAccountTypeDetailMode = 'traveler';
                    }}
                  >
                    出張者ごと
                  </A>
                </ShareDetailModeLinkArea>
                <ShareDetailModeLinkArea>
                  <A
                    onClick={() => {
                      reserveInfo.expensesAccountTypeDetailMode = 'both';
                    }}
                  >
                    商品×出張者ごと
                  </A>
                </ShareDetailModeLinkArea>
              </ShareDetailFlexArea>
              {reserveInfo.reservingTrip.isPackage ? (
                <ShareDetailFlexArea>
                  <ShareDetailLabel>パッケージ</ShareDetailLabel>
                  <ShareDetailSelectArea>
                    <InputCustomerInformationFilterableSelector
                      placeholder="勘定科目を検索"
                      options={reserveInfo.expensesAccountTypeOptions}
                      selected={{
                        label:
                          reserveInfo.orderItemMappingsExpensesPart.find(
                            m => m.travelerId === -1 && m.orderItemType === 'package' && m.orderItemIndex === 1
                          )?.selectedOptionLabel || '',
                        value: '',
                        matcher: ''
                      }}
                      onSelect={option => {
                        const mapping = reserveInfo.orderItemMappingsExpensesPart.find(
                          m => m.travelerId === -1 && m.orderItemType === 'package' && m.orderItemIndex === 1
                        );
                        if (mapping) {
                          onSelectDetail(mapping, option);
                        }
                      }}
                      isRequired={reserveInfo.isExpensesAccountTypeRequired}
                      workflowFormWidth={DETAIL_FORM_WIDTH}
                    />
                  </ShareDetailSelectArea>
                </ShareDetailFlexArea>
              ) : (
                <>
                  {reserveInfo.itemList.map((item, i: number) => (
                    <div key={`item${i}`}>
                      <ShareDetailFlexArea>
                        {item?.elementType === 'hotel' ? (
                          <ShareDetailLabel>{i + 1}. ホテル</ShareDetailLabel>
                        ) : (
                          <ShareDetailLabel>{i + 1}. 経路</ShareDetailLabel>
                        )}
                        <ShareDetailSelectArea>
                          <InputCustomerInformationFilterableSelector
                            placeholder="勘定科目を検索"
                            options={reserveInfo.expensesAccountTypeOptions}
                            selected={{
                              label:
                                reserveInfo.orderItemMappingsExpensesPart.find(
                                  m =>
                                    m.travelerId === -1 &&
                                    m.orderItemType === 'item' &&
                                    m.orderItemIndex === Number(item?.index)
                                )?.selectedOptionLabel || '',
                              value: '',
                              matcher: ''
                            }}
                            onSelect={option => {
                              const mapping = reserveInfo.orderItemMappingsExpensesPart.find(
                                m =>
                                  m.travelerId === -1 &&
                                  m.orderItemType === 'item' &&
                                  m.orderItemIndex === Number(item?.index)
                              );
                              if (mapping) {
                                onSelectDetail(mapping, option);
                              }
                            }}
                            isRequired={reserveInfo.isExpensesAccountTypeRequired}
                            workflowFormWidth={DETAIL_FORM_WIDTH}
                          />
                        </ShareDetailSelectArea>
                      </ShareDetailFlexArea>
                    </div>
                  ))}
                </>
              )}
              {reserveInfo.rentalCarRequired === 'true' && (
                <>
                  {reserveInfo.reservingTrip.rentalCars.map((rentalCar, car_index) => (
                    <ShareDetailFlexArea key={`car${car_index}`}>
                      <ShareDetailLabel>レンタカー {car_index + 1}</ShareDetailLabel>
                      <ShareDetailSelectArea>
                        <InputCustomerInformationFilterableSelector
                          placeholder="勘定科目を検索"
                          options={reserveInfo.expensesAccountTypeOptions}
                          selected={{
                            label:
                              reserveInfo.orderItemMappingsExpensesPart.find(
                                m =>
                                  m.travelerId === -1 &&
                                  m.orderItemType === 'rental_car' &&
                                  m.orderItemIndex === car_index
                              )?.selectedOptionLabel || '',
                            value: '',
                            matcher: ''
                          }}
                          onSelect={option => {
                            const mapping = reserveInfo.orderItemMappingsExpensesPart.find(
                              m =>
                                m.travelerId === -1 &&
                                m.orderItemType === 'rental_car' &&
                                m.orderItemIndex === car_index
                            );
                            if (mapping) {
                              onSelectDetail(mapping, option);
                            }
                          }}
                          isRequired={reserveInfo.isExpensesAccountTypeRequired}
                          workflowFormWidth={DETAIL_FORM_WIDTH}
                        />
                      </ShareDetailSelectArea>
                    </ShareDetailFlexArea>
                  ))}
                </>
              )}
            </>
          ) : reserveInfo.expensesAccountTypeDetailMode === 'traveler' ? (
            <>
              <ShareDetailFlexArea>
                <>
                  <ShareDetailModeLinkArea>
                    <A
                      onClick={() => {
                        reserveInfo.expensesAccountTypeDetailMode = 'item';
                      }}
                    >
                      商品ごと
                    </A>
                  </ShareDetailModeLinkArea>
                  <ShareDetailModeSelected>出張者ごと</ShareDetailModeSelected>
                  <ShareDetailModeLinkArea>
                    <A
                      onClick={() => {
                        reserveInfo.expensesAccountTypeDetailMode = 'both';
                      }}
                    >
                      商品×出張者ごと
                    </A>
                  </ShareDetailModeLinkArea>
                </>
              </ShareDetailFlexArea>
              {reserveInfo.reservingTrip.travelers.map((traveler, traveler_index) => (
                <ShareDetailFlexArea key={traveler_index}>
                  <MappingDetailLabel>{traveler.travelerName}</MappingDetailLabel>
                  <ShareDetailSelectArea>
                    <InputCustomerInformationFilterableSelector
                      placeholder="勘定科目を検索"
                      options={reserveInfo.expensesAccountTypeOptions}
                      selected={{
                        label:
                          reserveInfo.orderItemMappingsExpensesPart.find(
                            m =>
                              m.travelerId === traveler_index &&
                              m.orderItemType === 'all' &&
                              m.orderItemIndex === -1
                          )?.selectedOptionLabel || '',
                        value: '',
                        matcher: ''
                      }}
                      onSelect={option => {
                        const mapping = reserveInfo.orderItemMappingsExpensesPart.find(
                          m =>
                            m.travelerId === traveler_index && m.orderItemType === 'all' && m.orderItemIndex === -1
                        );
                        if (mapping) {
                          onSelectDetail(mapping, option);
                        }
                      }}
                      isRequired={reserveInfo.isExpensesAccountTypeRequired}
                      workflowFormWidth={DETAIL_FORM_WIDTH}
                    />
                  </ShareDetailSelectArea>
                </ShareDetailFlexArea>
              ))}
            </>
          ) : reserveInfo.expensesAccountTypeDetailMode === 'both' ? (
            <>
              <ShareDetailFlexArea>
                <ShareDetailModeLinkArea>
                  <A
                    onClick={() => {
                      reserveInfo.expensesAccountTypeDetailMode = 'item';
                    }}
                  >
                    商品ごと
                  </A>
                </ShareDetailModeLinkArea>
                <ShareDetailModeLinkArea>
                  <A
                    onClick={() => {
                      reserveInfo.expensesAccountTypeDetailMode = 'traveler';
                    }}
                  >
                    出張者ごと
                  </A>
                </ShareDetailModeLinkArea>
                <ShareDetailModeSelected>商品×出張者ごと</ShareDetailModeSelected>
              </ShareDetailFlexArea>
              {reserveInfo.reservingTrip.isPackage ? (
                <>
                  <MappingDetailLabel>パッケージ</MappingDetailLabel>
                  <ShareDetailIndentedArea>
                    {reserveInfo.reservingTrip.travelers.map((traveler, traveler_index) => (
                      <ShareDetailFlexArea key={traveler_index}>
                        <MappingDetailLabel>{traveler.travelerName}</MappingDetailLabel>
                        <ShareDetailSelectArea>
                          <InputCustomerInformationFilterableSelector
                            placeholder="勘定科目を検索"
                            options={reserveInfo.expensesAccountTypeOptions}
                            selected={{
                              label:
                                reserveInfo.orderItemMappingsExpensesPart.find(
                                  m =>
                                    m.travelerId === traveler_index &&
                                    m.orderItemType === 'package' &&
                                    m.orderItemIndex === 1
                                )?.selectedOptionLabel || '',
                              value: '',
                              matcher: ''
                            }}
                            onSelect={option => {
                              const mapping = reserveInfo.orderItemMappingsExpensesPart.find(
                                m =>
                                  m.travelerId === traveler_index &&
                                  m.orderItemType === 'package' &&
                                  m.orderItemIndex === 1
                              );
                              if (mapping) {
                                onSelectDetail(mapping, option);
                              }
                            }}
                            isRequired={reserveInfo.isExpensesAccountTypeRequired}
                            workflowFormWidth={DETAIL_FORM_WIDTH}
                          />
                        </ShareDetailSelectArea>
                      </ShareDetailFlexArea>
                    ))}
                  </ShareDetailIndentedArea>
                </>
              ) : (
                <>
                  {reserveInfo.itemList.map((item, i: number) => (
                    <div key={`item${i}`}>
                      {item?.elementType === 'hotel' ? (
                        <MappingDetailLabel>{i + 1}. ホテル</MappingDetailLabel>
                      ) : (
                        <MappingDetailLabel>{i + 1}. 経路</MappingDetailLabel>
                      )}
                      <ShareDetailIndentedArea>
                        {reserveInfo.reservingTrip.travelers.map((traveler, traveler_index) => (
                          <ShareDetailFlexArea key={traveler_index}>
                            <MappingDetailLabel>{traveler.travelerName}</MappingDetailLabel>
                            <ShareDetailSelectArea>
                              <InputCustomerInformationFilterableSelector
                                placeholder="勘定科目を検索"
                                options={reserveInfo.expensesAccountTypeOptions}
                                selected={{
                                  label:
                                    reserveInfo.orderItemMappingsExpensesPart.find(
                                      m =>
                                        m.travelerId === traveler_index &&
                                        m.orderItemType === 'item' &&
                                        m.orderItemIndex === Number(item?.index)
                                    )?.selectedOptionLabel || '',
                                  value: '',
                                  matcher: ''
                                }}
                                onSelect={option => {
                                  const mapping = reserveInfo.orderItemMappingsExpensesPart.find(
                                    m =>
                                      m.travelerId === traveler_index &&
                                      m.orderItemType === 'item' &&
                                      m.orderItemIndex === Number(item?.index)
                                  );
                                  if (mapping) {
                                    onSelectDetail(mapping, option);
                                  }
                                }}
                                isRequired={reserveInfo.isExpensesAccountTypeRequired}
                                workflowFormWidth={DETAIL_FORM_WIDTH}
                              />
                            </ShareDetailSelectArea>
                          </ShareDetailFlexArea>
                        ))}
                      </ShareDetailIndentedArea>
                    </div>
                  ))}
                </>
              )}
              {reserveInfo.rentalCarRequired === 'true' && (
                <>
                  {reserveInfo.reservingTrip.rentalCars.map((rentalCar, car_index) => (
                    <div key={`car${car_index}`}>
                      <MappingDetailLabel>レンタカー {car_index + 1}</MappingDetailLabel>
                      <ShareDetailIndentedArea>
                        {reserveInfo.reservingTrip.travelers.map((traveler, traveler_index) => (
                          <ShareDetailFlexArea key={traveler_index}>
                            <MappingDetailLabel>{traveler.travelerName}</MappingDetailLabel>
                            <ShareDetailSelectArea>
                              <InputCustomerInformationFilterableSelector
                                placeholder="勘定科目を検索"
                                options={reserveInfo.expensesAccountTypeOptions}
                                selected={{
                                  label:
                                    reserveInfo.orderItemMappingsExpensesPart.find(
                                      m =>
                                        m.travelerId === traveler_index &&
                                        m.orderItemType === 'rental_car' &&
                                        m.orderItemIndex === car_index
                                    )?.selectedOptionLabel || '',
                                  value: '',
                                  matcher: ''
                                }}
                                onSelect={option => {
                                  const mapping = reserveInfo.orderItemMappingsExpensesPart.find(
                                    m =>
                                      m.travelerId === traveler_index &&
                                      m.orderItemType === 'rental_car' &&
                                      m.orderItemIndex === car_index
                                  );
                                  if (mapping) {
                                    onSelectDetail(mapping, option);
                                  }
                                }}
                                isRequired={reserveInfo.isExpensesAccountTypeRequired}
                                workflowFormWidth={DETAIL_FORM_WIDTH}
                              />
                            </ShareDetailSelectArea>
                          </ShareDetailFlexArea>
                        ))}
                      </ShareDetailIndentedArea>
                    </div>
                  ))}
                </>
              )}
            </>
          ) : null}
          <DetailLinkArea>
            <A
              onClick={() => {
                reserveInfo.showExpensesAccountTypeDetail = false;
                setShowDetail(false);
              }}
            >
              キャンセル
            </A>
          </DetailLinkArea>
        </ShareDetailArea>
      ) : (
        <>
          <InputAreaRight data-testid="expenses_account_type_select" workflowFormWidth={workflowFormWidth}>
            <InputCustomerInformationFilterableSelector
              placeholder="勘定科目を検索"
              options={reserveInfo.expensesAccountTypeOptions}
              selected={selectedOption()}
              onSelect={onSelect}
              isRequired={reserveInfo.isExpensesAccountTypeRequired}
              workflowFormWidth={workflowFormWidth}
            />
          </InputAreaRight>
          <DetailLinkArea>
            <A
              onClick={() => {
                reserveInfo.showExpensesAccountTypeDetail = true;
                reserveInfo.initOrderItemMappings('expenses_account_type');
                setShowDetail(true);
              }}
            >
              詳細設定
            </A>
          </DetailLinkArea>
        </>
      )}
    </>
  );
};

const Label = styled.div`
  font-size: 13px;
  width: 200px;
  line-height: 34px;
`;

const InputAreaRight = styled.div<{ workflowFormWidth?: string }>`
  line-height: 34px;
  display: flex;
  width: ${props => props.workflowFormWidth || '420px'};
`;
const DetailLinkArea = styled.div`
  font-weight: bold;
  margin-top: 5px;
  margin-left: 8px;
`;

const ShareDetailModeLinkArea = styled.div`
  font-weight: bold;
  margin-top: 8px;
  margin-right: 16px;
`;

const ShareDetailModeSelected = styled.p`
  font-weight: bold;
  margin-top: 8px;
  margin-right: 16px;
`;

const ShareDetailArea = styled.div<{ workflowFormWidth?: string }>`
  display: block;
  margin-bottom: 24px;
  width: ${props => props.workflowFormWidth || '420px'};
`;

const ShareDetailFlexArea = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const MappingDetailLabel = styled.p`
  font-weight: bold;
  margin-top: 4px;
  margin-left: 8px;
`;

const ShareDetailLabel = styled.p`
  font-weight: bold;
  margin-top: 4px;
  margin-left: 8px;
  width: 60px;
`;

const ShareDetailIndentedArea = styled.div`
  margin-top: 8px;
  margin-left: 16px;
  width: ${DETAIL_FORM_WIDTH};
`;

const ShareDetailSelectArea = styled.div`
  width: ${DETAIL_FORM_WIDTH};
`;

const Red = styled.span`
  color: ${props => props.theme.redColor};
`;

export default ExpensesAccountTypeSection;
